import { HttpStatusCode } from "axios";
import z from "zod";

/**
 * Why `DELETE work/draft-proposals` did not delete a draft it was asked to delete
 * (`Work.Proposals.DraftDeletionRefusal`, #6577).
 *
 * - `unauthorized` — the reader did not create it. Only a draft's creator may delete it.
 * - `submitted` — it was sent, so it is no longer a draft. It is cancelled instead.
 * - `missing` — it no longer exists.
 * - `interrupted` — the delete stopped before it: it, or an earlier draft, changed while the
 *   delete ran. Trying again may delete it.
 */
export enum DraftDeletionRefusal {
  Unauthorized = "unauthorized",
  Submitted = "submitted",
  Missing = "missing",
  Interrupted = "interrupted",
}

export const DraftDeletionResultJSONZ = z.object({
  deleted: z.array(z.string()),
  unavailable: z.array(z.object({ id: z.string(), reason: z.enum(DraftDeletionRefusal) })),
});

/** Every id the delete was given: deleted, or not deleted with its reason. */
export type DraftDeletionResultJSON = z.infer<typeof DraftDeletionResultJSONZ>;

/** Why one draft was not deleted, as a clause that reads after "not deleted:". Singular, then plural. */
const refusalClauses: Record<DraftDeletionRefusal, readonly [string, string]> = {
  [DraftDeletionRefusal.Unauthorized]: ["you are not its creator", "you are not their creator"],
  [DraftDeletionRefusal.Submitted]: ["it was already submitted", "they were already submitted"],
  [DraftDeletionRefusal.Missing]: ["it no longer exists", "they no longer exist"],
  [DraftDeletionRefusal.Interrupted]: ["the delete was interrupted; try again", "the delete was interrupted; try again"],
};

/** The label under a row that the delete refused, e.g. "Not deleted: you are not its creator". */
export function draftDeletionRefusalLabel(reason: DraftDeletionRefusal): string {
  return `Not deleted: ${refusalClauses[reason][0]}`;
}

/** What the draft page says when its delete is refused, e.g. "Draft not deleted: you are not its creator." */
export function singleDraftDeletionRefusalMessage(reason: DraftDeletionRefusal): string {
  return `Draft not deleted: ${refusalClauses[reason][0]}.`;
}

/**
 * One sentence per reason, e.g. "2 drafts not deleted: you are not their creator. 1 draft not
 * deleted: it no longer exists."
 */
export function draftDeletionRefusalSummary(unavailable: DraftDeletionResultJSON["unavailable"]): string {
  return Object.values(DraftDeletionRefusal)
    .map((reason) => [reason, unavailable.filter((id) => id.reason === reason).length] as const)
    .filter(([, count]) => count > 0)
    .map(([reason, count]) =>
      count === 1
        ? `1 draft not deleted: ${refusalClauses[reason][0]}.`
        : `${count} drafts not deleted: ${refusalClauses[reason][1]}.`,
    )
    .join(" ");
}

/**
 * The refusal behind a status from `DELETE work/proposals/{id}`, which answers one draft by status
 * rather than with a reason: 403 when the reader did not create it, 400 when it was submitted, 404
 * when it is gone, and 409 when it changed while it was being deleted. Any other status is not a
 * refusal.
 */
export function draftDeletionRefusalFromStatus(status: unknown): DraftDeletionRefusal | undefined {
  switch (status) {
    case HttpStatusCode.Forbidden:
      return DraftDeletionRefusal.Unauthorized;
    case HttpStatusCode.BadRequest:
      return DraftDeletionRefusal.Submitted;
    case HttpStatusCode.NotFound:
      return DraftDeletionRefusal.Missing;
    case HttpStatusCode.Conflict:
      return DraftDeletionRefusal.Interrupted;
    default:
      return undefined;
  }
}

/**
 * Why no proposal in the list can be selected: selection exists only to delete drafts, so it is
 * enabled under the Drafts stage chip alone. The checkboxes stay in place, disabled, and say so (#6575).
 */
export const draftSelectionOutsideDraftsReason = "Select only the Drafts filter to choose proposals to delete";

/**
 * Why a draft in the list cannot be selected for deletion: the server did not offer this reader
 * Delete on it, and it offers Delete to the draft's creator alone (`AwaitingSubmissionStatus`).
 */
export function draftDeletionWithheldReason(creatorName: string | undefined): string {
  return creatorName
    ? `Only the creator, ${creatorName}, can delete this draft`
    : "Only the creator can delete this draft";
}
